import React, { useContext, useEffect } from 'react';
import {
  ModalFooter,
  HStack,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useToast,
  VStack,
  SimpleGrid,
} from '@chakra-ui/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from '../../../../components/Form/Input';
import { MenuLocationsContext } from '../../../../hooks/useMenuItemsLocation';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (location: any) => void;
  onEdited?: (location: any) => void;
  locationToEdit?: null | any;
}

interface FormData {
  name: string;
}

const createFormData = yup.object().shape({
  name: yup.string().required('Nome é obrigatório'),
});

const MenuItemsLocationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  locationToEdit,
}) => {
  const { formState, handleSubmit, register, setValue, reset } = useForm({
    resolver: yupResolver(createFormData),
  });

  const toast = useToast();
  const { errors } = formState;

  const { createLocation, updateLocation, loading } =
    useContext(MenuLocationsContext);

  useEffect(() => {
    if (!locationToEdit) return;
    setValue('name', locationToEdit.name);
    setValue('slug', locationToEdit.slug);
  }, [locationToEdit, setValue]);

  const handleForm: SubmitHandler<FormData> = async data => {
    try {
      if (!locationToEdit) {
        createLocation(data);
        toast({
          title: 'Sucesso',
          description: 'Item criado com sucesso',
          status: 'success',
          position: 'top-right',
        });
      } else {
        updateLocation(locationToEdit.id, data);
        toast({
          title: 'Sucesso',
          description: 'Item editado com sucesso',
          status: 'success',
          position: 'top-right',
        });
      }
      reset();
      onClose();
    } catch (err: any) {
      toast({
        title: 'Opss!!',
        description:
          err?.response?.data?.message ||
          'Tivemos um erro, tente novamente mais tarde',
        status: 'error',
        position: 'top-right',
      });
    }
  };

  return (
    <Modal
      onClose={() => {
        onClose();
        reset();
      }}
      isOpen={isOpen}
      isCentered
      size="xl"
    >
      <ModalOverlay />
      <ModalContent maxH="90vh" as="form" onSubmit={handleSubmit(handleForm)}>
        <ModalHeader>Localização de itens de menu</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={8}>
            <SimpleGrid minChildWidth="200px" spacing="8" w="100%">
              <Input
                isRequired
                label="Nome"
                error={errors.name}
                {...register('name')}
              />
            </SimpleGrid>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={4}>
            <Button onClick={onClose}>Fechar</Button>
            <Button colorScheme="brand" isLoading={loading} type="submit">
              Salvar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MenuItemsLocationModal;
